
import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  ShoppingCart, 
  Package, 
  TrendingUp, 
  Calendar, 
  Megaphone, 
  BarChart3, 
  Settings,
  ChevronRight,
  Building2,
  LogOut,
  Menu,
  X,
  ClipboardList,
  Archive,
  LineChart,
  Upload,
  ShoppingBag,
  AlertTriangle,
  CalendarDays,
  Zap,
  Search,
  PieChart,
  Activity,
  FileBarChart,
  Cog,
  ChevronDown,
  DollarSign,
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ROUTES } from '@/lib/routes';
import { useCompanyFeatureFlag } from '@/hooks/useCompanyFeatureFlag';
import { ShoppingCart as HQPurchasingIcon, Truck, Calculator } from 'lucide-react';

interface ModuleItem {
  id: string;
  titleKey: string;
  icon: React.ComponentType<any>;
  category: string;
  descriptionKey: string;
  badge?: string;
  route?: string;
  /** When set, this module is only shown when company has this feature flag */
  featureKey?: string;
}

interface CategoryProps {
  id: string;
  titleKey: string;
  subtitleKey: string;
  icon: React.ComponentType<any>;
  modules: ModuleItem[];
}

interface AutoHideSidebarProps {
  selectedCategory: string;
  setSelectedCategory: (category: string) => void;
  userEmail: string;
  companyName: string;
}

const AutoHideSidebar: React.FC<AutoHideSidebarProps> = ({
  selectedCategory,
  setSelectedCategory,
  userEmail,
  companyName
}) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<string[]>([selectedCategory]);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const { t } = useLanguage();
  const navigate = useNavigate();

  // 同步 expandedCategories 与 selectedCategory，确保选中分类总是展开
  useEffect(() => {
    if (selectedCategory) {
      setExpandedCategories(prev => {
        if (!prev.includes(selectedCategory)) {
          return [...prev, selectedCategory];
        }
        return prev;
      });
    }
  }, [selectedCategory]);

  const modules: ModuleItem[] = [
    // Order Management
    { id: 'order-overview', titleKey: 'orderOverview', icon: ClipboardList, category: 'order', descriptionKey: 'orderOverview', route: ROUTES.order.overview },
    { id: 'sales-stats', titleKey: 'salesStats', icon: BarChart3, category: 'order', descriptionKey: 'salesStats', route: ROUTES.order.statistics },
    { id: 'sales-import', titleKey: 'salesImport', icon: Upload, category: 'order', descriptionKey: 'salesImport', route: ROUTES.order.import },
    
    // Product Overview
    { id: 'product-mgmt', titleKey: 'productMgmt', icon: Package, category: 'product', descriptionKey: 'productMgmt', route: ROUTES.product.overview.management },
    { id: 'inventory-mgmt', titleKey: 'inventoryMgmt', icon: Archive, category: 'product', descriptionKey: 'inventoryMgmt', route: ROUTES.product.overview.stock },
    { id: 'product-stats', titleKey: 'productStats', icon: PieChart, category: 'product', descriptionKey: 'productStats', route: ROUTES.product.overview.statistics },
    { id: 'product-import', titleKey: 'productImport', icon: Upload, category: 'product', descriptionKey: 'productImport', route: ROUTES.product.overview.import },
    { id: 'product-stock-import', titleKey: 'productStockImport', icon: Upload, category: 'product', descriptionKey: 'productStockImport', route: ROUTES.product.overview.importStock },
    { id: 'product-pending-import', titleKey: 'productPendingImport', icon: Upload, category: 'product', descriptionKey: 'productPendingImport', route: ROUTES.product.overview.importPending },
    { id: 'product-3pl-import', titleKey: 'product3PLDataImport', icon: Upload, category: 'product', descriptionKey: 'product3PLDataImport', route: ROUTES.product.overview.import3PL },
    
    // Purchase Suggestions
    { id: 'suggested-orders', titleKey: 'suggestedOrders', icon: ShoppingBag, category: 'purchase', descriptionKey: 'suggestedOrders', route: ROUTES.purchase.suggestedOrders },
    { id: 'cross-company-suggestions', titleKey: 'crossCompanySuggestions', icon: Building2, category: 'purchase', descriptionKey: 'crossCompanySuggestions', route: ROUTES.purchase.crossCompanySuggestions },
    { id: 'rule-settings', titleKey: 'ruleSettings', icon: Settings, category: 'purchase', descriptionKey: 'ruleSettings', route: ROUTES.purchase.ruleSettings },
    
    // Expiry Summary
    { id: 'expiring-soon', titleKey: 'expiringSoon', icon: AlertTriangle, category: 'expiry', descriptionKey: 'expiringSoon', badge: 'Alert', route: ROUTES.expiry.expiringSoon },
    { id: 'expiry-handling', titleKey: 'expiryHandling', icon: CalendarDays, category: 'expiry', descriptionKey: 'expiryHandling', route: ROUTES.expiry.expiryProcessing },
    { id: 'expiry-import', titleKey: 'bestExpiryImport', icon: Upload, category: 'expiry', descriptionKey: 'bestExpiryImport', route: ROUTES.expiry.bestExpiryImport },
    
    // Promotions
    { id: 'campaign-overview', titleKey: 'campaignOverview', icon: Megaphone, category: 'promotion', descriptionKey: 'campaignOverview' },
    { id: 'search-campaigns', titleKey: 'searchCampaigns', icon: Search, category: 'promotion', descriptionKey: 'searchCampaigns' },
    { id: 'promotion-import', titleKey: 'promotionImport', icon: Upload, category: 'promotion', descriptionKey: 'promotionImport' },
    
    // Analytics
    { id: 'sales-analysis', titleKey: 'salesAnalysis', icon: LineChart, category: 'analytics', descriptionKey: 'salesAnalysis', route: ROUTES.analytics.salesAnalysis },
    { id: 'stock-analysis', titleKey: 'stockAnalysis', icon: FileBarChart, category: 'analytics', descriptionKey: 'stockAnalysis', route: ROUTES.product.overview.statistics },
    { id: 'slow-moving', titleKey: 'slowMoving', icon: Activity, category: 'analytics', descriptionKey: 'slowMoving', route: ROUTES.analytics.slowMoving },
    { id: 'traffic-analysis', titleKey: 'trafficAnalysis', icon: TrendingUp, category: 'analytics', descriptionKey: 'trafficAnalysis', route: ROUTES.analytics.trafficAnalysis },
    { id: 'price-analysis', titleKey: 'priceAnalysis', icon: DollarSign, category: 'analytics', descriptionKey: 'priceAnalysis', route: ROUTES.analytics.priceAnalysis },
    
    // Store Settings
    { id: 'store-settings', titleKey: 'storeMgmt', icon: Cog, category: 'settings', descriptionKey: 'storeMgmt' },

    // Headquarters (shown only when company_feature_flags has headquarters_module)
    { id: 'hq-purchasing', titleKey: 'hqPurchasing', icon: HQPurchasingIcon, category: 'hq', descriptionKey: 'hqPurchasing', route: ROUTES.hq.purchasing, featureKey: 'headquarters_module' },
    { id: 'hq-wholesale', titleKey: 'hqWholesale', icon: Truck, category: 'hq', descriptionKey: 'hqWholesale', route: ROUTES.hq.wholesale, featureKey: 'headquarters_module' },
    { id: 'hq-accounting', titleKey: 'hqAccounting', icon: Calculator, category: 'hq', descriptionKey: 'hqAccounting', route: ROUTES.hq.accounting, featureKey: 'headquarters_module' },
  ];

  const { enabled: hqEnabled, loading: hqFlagLoading } = useCompanyFeatureFlag();

  const baseCategories: CategoryProps[] = [
    { id: 'order', titleKey: 'orderManagement', subtitleKey: 'orderManagement', icon: ShoppingCart, modules: modules.filter(m => m.category === 'order') },
    { id: 'product', titleKey: 'productOverview', subtitleKey: 'productOverview', icon: Package, modules: modules.filter(m => m.category === 'product') },
    { id: 'purchase', titleKey: 'purchaseSuggestions', subtitleKey: 'purchaseSuggestions', icon: TrendingUp, modules: modules.filter(m => m.category === 'purchase') },
    { id: 'expiry', titleKey: 'expirySummary', subtitleKey: 'expirySummary', icon: Calendar, modules: modules.filter(m => m.category === 'expiry') },
    { id: 'promotion', titleKey: 'promotions', subtitleKey: 'promotions', icon: Megaphone, modules: modules.filter(m => m.category === 'promotion') },
    { id: 'analytics', titleKey: 'analytics', subtitleKey: 'analytics', icon: BarChart3, modules: modules.filter(m => m.category === 'analytics') },
    { id: 'settings', titleKey: 'storeSettings', subtitleKey: 'storeSettings', icon: Settings, modules: modules.filter(m => m.category === 'settings') },
  ];

  const hqCategory: CategoryProps = {
    id: 'hq',
    titleKey: 'headquarters',
    subtitleKey: 'headquarters',
    icon: Building2,
    modules: modules.filter(m => m.category === 'hq'),
  };

  const categories: CategoryProps[] = hqFlagLoading
    ? baseCategories
    : hqEnabled
      ? [...baseCategories, hqCategory]
      : baseCategories;

  // Auto-hide functionality
  useEffect(() => {
    const handleMouseEnter = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      setSidebarCollapsed(false);
    };

    const handleMouseLeave = () => {
      timeoutRef.current = setTimeout(() => {
        setSidebarCollapsed(true);
      }, 10000); // 10 seconds
    };

    const sidebarElement = sidebarRef.current;
    if (sidebarElement) {
      sidebarElement.addEventListener('mouseenter', handleMouseEnter);
      sidebarElement.addEventListener('mouseleave', handleMouseLeave);
    }

    // Initial timeout
    timeoutRef.current = setTimeout(() => {
      setSidebarCollapsed(true);
    }, 10000);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (sidebarElement) {
        sidebarElement.removeEventListener('mouseenter', handleMouseEnter);
        sidebarElement.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    toast.success(t('logout'));
    navigate('/login');
  };

  const handleBackToCompanies = () => {
    localStorage.removeItem('selectedCompany');
    localStorage.removeItem('selectedCompanyName');
    // 使用 window.location.assign 強制重新載入頁面，確保公司列表正確載入
    window.location.assign('/company-selection');
  };

  const handleModuleClick = (module: ModuleItem) => {
    if (module.route) {
      // Navigate and force hard reload to avoid any SPA state issues
      navigate(module.route);
      setTimeout(() => {
        if (window.location.pathname !== module.route) {
          window.location.assign(module.route);
        }
      }, 0);
      return;
    }
    if (import.meta.env.DEV) {
      toast.error(`Missing route for module: ${module.id}`);
    } else {
      toast.info(`${t('opening')} ${t(module.titleKey)}...`);
    }
  };

  const toggleCategoryExpansion = (categoryId: string) => {
    setExpandedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleCategoryClick = (categoryId: string) => {
    const isCurrentlySelected = selectedCategory === categoryId;
    const isCurrentlyExpanded = expandedCategories.includes(categoryId);
    
    // 如果侧边栏已折叠，先展开它
    if (sidebarCollapsed) {
      setSidebarCollapsed(false);
      // 清除自动隐藏定时器
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = undefined;
      }
      // 如果侧边栏刚展开，确保分类也被展开
      if (!isCurrentlyExpanded) {
        setExpandedCategories(prev => [...prev, categoryId]);
      }
      // 更新选中分类
      setSelectedCategory(categoryId);
    } else {
      // 如果侧边栏未折叠
      if (isCurrentlySelected) {
        // 如果点击的是当前选中的分类，切换展开/收起状态
        toggleCategoryExpansion(categoryId);
      } else {
        // 如果点击的是不同的分类，更新选中分类并展开它
        setSelectedCategory(categoryId);
        if (!isCurrentlyExpanded) {
          setExpandedCategories(prev => [...prev, categoryId]);
        }
      }
    }
  };

  const handleManualToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
    // Clear any existing timeout when manually toggling
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    // Restart the auto-hide timer if expanding
    if (sidebarCollapsed) {
      timeoutRef.current = setTimeout(() => {
        setSidebarCollapsed(true);
      }, 10000);
    }
  };

  return (
    <div 
      ref={sidebarRef}
      className={`${sidebarCollapsed ? 'w-16' : 'w-80'} transition-all duration-300 bg-sidebar border-r border-sidebar-border flex flex-col`}
    >
      {/* Sidebar Header */}
      <div className="p-4 border-b border-sidebar-border">
        <div className="flex items-center justify-between">
          <div className={`${sidebarCollapsed ? 'hidden' : 'flex'} items-center gap-3`}>
            <Building2 className="w-8 h-8 text-purple-400" />
            <div>
              <h2 className="text-sidebar-foreground font-semibold text-lg">{t('erpSystem')}</h2>
              <p className="text-muted-foreground text-sm">{companyName}</p>
            </div>
          </div>
          
          {sidebarCollapsed && (
            <div className="flex justify-center w-full">
              <Building2 className="w-8 h-8 text-purple-400" />
            </div>
          )}
          
          <Button
            variant="ghost"
            size="sm"
            onClick={handleManualToggle}
            className={`text-sidebar-foreground hover:text-sidebar-accent-foreground hover:bg-sidebar-accent flex-shrink-0 ${sidebarCollapsed ? 'w-full justify-center' : ''}`}
          >
            {sidebarCollapsed ? <Menu className="w-5 h-5" /> : <X className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex-1 p-4 space-y-2">
        {categories.map((category) => {
          const IconComponent = category.icon;
          const isSelected = selectedCategory === category.id;
          const isExpanded = expandedCategories.includes(category.id);
          
          return (
            <div key={category.id}>
              <Collapsible open={isExpanded && !sidebarCollapsed}>
                <CollapsibleTrigger asChild>
                  <button
                    onClick={() => handleCategoryClick(category.id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all duration-200 ${
                      isSelected 
                        ? 'bg-sidebar-primary text-sidebar-primary-foreground shadow-lg' 
                        : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                    }`}
                  >
                    <div className={`flex items-center ${sidebarCollapsed ? 'justify-center w-full' : 'justify-start'}`}>
                      <IconComponent className="w-5 h-5 flex-shrink-0" />
                    </div>
                    {!sidebarCollapsed && (
                      <>
                        <div className="flex-1 text-left">
                          <div className="font-medium">{t(category.titleKey)}</div>
                        </div>
                        <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      </>
                    )}
                  </button>
                </CollapsibleTrigger>
                
                <CollapsibleContent className="mt-2 ml-4 space-y-1">
                  {category.modules.map((module) => {
                    const ModuleIcon = module.icon;
                    const route = module.route;
                    const content = (
                      <div className="w-full flex items-center gap-3 p-2 text-sm rounded-md text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors">
                        <ModuleIcon className="w-4 h-4" />
                        <span className="flex-1 text-left">{t(module.titleKey)}</span>
                        {module.badge && (
                          <Badge variant="secondary" className="bg-purple-600/20 text-purple-400 border-purple-600/30 text-xs">
                            {module.badge}
                          </Badge>
                        )}
                      </div>
                    );
                    if (route) {
                      return (
                        <a
                          key={module.id}
                          href={route}
                          className="block"
                          onClick={(e) => {
                            // Force hard navigation to avoid SPA state issues
                            e.preventDefault();
                            window.location.href = route;
                          }}
                        >
                          {content}
                        </a>
                      );
                    }
                    return (
                      <button
                        key={module.id}
                        onClick={() => handleModuleClick(module)}
                        className="w-full text-left"
                      >
                        {content}
                      </button>
                    );
                  })}
                </CollapsibleContent>
              </Collapsible>
            </div>
          );
        })}
      </div>

      {/* Sidebar Footer */}
      <div className="p-4 border-t border-sidebar-border space-y-2">
        {!sidebarCollapsed && (
          <>
            <LanguageSwitcher />
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBackToCompanies}
              className="w-full justify-start text-muted-foreground hover:text-foreground"
            >
              <Building2 className="w-4 h-4 mr-2" />
              {t('switchCompany')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="w-full justify-start text-muted-foreground hover:text-foreground"
            >
              <LogOut className="w-4 h-4 mr-2" />
              {t('logout')}
            </Button>
          </>
        )}
        
        {sidebarCollapsed && (
          <div className="flex flex-col items-center space-y-2">
            <div className="w-full flex justify-center">
              <LanguageSwitcher />
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBackToCompanies}
              className="w-full flex justify-center text-muted-foreground hover:text-foreground"
            >
              <Building2 className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="w-full flex justify-center text-muted-foreground hover:text-foreground"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AutoHideSidebar;
